import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  styled,
  tableCellClasses,
} from "@mui/material";
import { Form, Formik, FormikProps } from "formik";
import * as Yup from "yup";
import Paragraph from "../../components/Paragraph";
import AppSelect from "../../components/AppSelect";
import { useContext, useState } from "react";
import CandidateWork from "../../models/CandidateWork";
import AppTextField from "../../components/AppTextField";
import AppPhoneField from "../../components/AppPhoneField";
import candidateService from "../../services/CandidateService";
import ConfirmationDialog from "../../components/ConfirmationDialog";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import CandidatePreviousJobModal from "./CandidatePreviousJobModal";
import LoadingModal from "../../components/LoadingModal";
import { handleInputChangeToUppercase } from "../../Utils";
import { CandidateApplicationContext } from "../../screens/CandidateApplicationScreen";
import { StyledTableCell, StyledTableRow } from "../../theme/GlobalTheme";
import { ApplicationState } from "../../models/ApplicationState";
import { useAppSelector } from "../../store/store";
import { useAppContext } from "../../context/AppContext";
import IOption from "../../interfaces/IOption";

interface ICandidateWorkForm {
  isCurrentlyWorking: string;
  candidateWorkId: number;
  recentWork: string | null;
  position: string | null;
  salary: string;
  companyDirection: string | null;
  companyDirection2: string | null;
  state: string | null;
  city: string | null;
  companyNeighborhood: string | null;
  workTime: string | null;
  companyPhone: string | null;
  companyPhoneCountryCode: string;
  supervisorName: string | null;
  actualJOB: boolean | null;
  hasPreviousJobs: string;
  workFunctionality: string;
  countryId?: number | null;
}

const candidateWorkFormValidationScheme = Yup.object().shape({
  isCurrentlyWorking: Yup.string().required("Requerido"),
  recentWork: Yup.string().when(
    "isCurrentlyWorking",
    (isCurrentlyWorking: string | string[], schema) => {
      if (isCurrentlyWorking === "true") {
        return schema.required("Requerido");
      }
      return schema;
    },
  ),
  position: Yup.string().when(
    "isCurrentlyWorking",
    (isCurrentlyWorking: string | string[], schema) => {
      if (isCurrentlyWorking[0] == "true") {
        return schema.required("Requerido");
      }
      return schema;
    },
  ),
  companyDirection: Yup.string().when(
    "isCurrentlyWorking",
    (isCurrentlyWorking: string | string[], schema) => {
      if (isCurrentlyWorking[0] == "true") {
        return schema.required("Requerido");
      }
      return schema;
    },
  ),
  countryId: Yup.string().when(
    "isCurrentlyWorking",
    (isCurrentlyWorking: string | string[], schema) => {
      if (isCurrentlyWorking[0] == "true") {
        return schema.required("Requerido");
      }
      return schema;
    },
  ),
  state: Yup.string().when(
    "isCurrentlyWorking",
    (isCurrentlyWorking: string | string[], schema) => {
      if (isCurrentlyWorking[0] == "true") {
        return schema.required("Requerido");
      }
      return schema;
    },
  ),
  city: Yup.string().when(
    "isCurrentlyWorking",
    (isCurrentlyWorking: string | string[], schema) => {
      if (isCurrentlyWorking[0] == "true") {
        return schema.required("Requerido");
      }
      return schema;
    },
  ),
  salary: Yup.string().when(
    "isCurrentlyWorking",
    (isCurrentlyWorking: string | string[], schema) => {
      if (isCurrentlyWorking[0] == "true") {
        return schema.required("Requerido");
      }
      return schema;
    },
  ),
  workTime: Yup.string().when(
    "isCurrentlyWorking",
    (isCurrentlyWorking: string | string[], schema) => {
      if (isCurrentlyWorking[0] == "true") {
        return schema.required("Requerido");
      }
      return schema;
    },
  ),
  companyPhone: Yup.string().when(
    "isCurrentlyWorking",
    (isCurrentlyWorking: string | string[], schema) => {
      if (isCurrentlyWorking[0] == "true") {
        return schema.required("Requerido");
      }
      return schema;
    },
  ),
  workFunctionality: Yup.string().when(
    "isCurrentlyWorking",
    (isCurrentlyWorking: string | string[], schema) => {
      if (isCurrentlyWorking[0] == "true") {
        return schema.required("Requerido");
      }
      return schema;
    },
  ),
});

function CandidateWorkForm() {
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogTitle, setDialogTitle] = useState("");
  const [dialogDesc, setDialogDesc] = useState("");
  const [dialogButton, setDialogButton] = useState<
    JSX.Element | JSX.Element[] | undefined
  >();
  const [candidatePreviousJobModal, setCandidatePreviousJobModal] =
    useState<boolean>(false);
  const [candidateWorks, setCandidateWorks] = useState<CandidateWork[]>([]);
  const [candidateWork, setCandidateWork] = useState<CandidateWork>({
    isCurrentlyWorking: false,
    candidateWorkId: 0,
    recentWork: "",
    position: "",
    salary: 0,
    companyDirection: "",
    city: "",
    state: "",
    companyNeighborhood: "",
    workTime: "",
    companyPhone: "",
    companyPhoneCountryCode: "",
    supervisorName: "",
    actualJOB: false,
  });
  const [isValidForm, setIsValidForm] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { step, setStep } = useContext(CandidateApplicationContext);
  const appState: ApplicationState = useAppSelector((state) => state.appState);
  const { countries } = useAppContext();
  const [initialValues, setInitialValues] = useState<ICandidateWorkForm>({
    isCurrentlyWorking: "false",
    candidateWorkId: 0,
    recentWork: "",
    position: "",
    salary: "",
    companyDirection: "",
    companyDirection2: "",
    city: "",
    state: "",
    companyNeighborhood: "",
    workTime: "",
    companyPhone: "",
    companyPhoneCountryCode: appState.countryCode,
    supervisorName: "",
    actualJOB: false,
    hasPreviousJobs: "NO",
    workFunctionality: "",
  });

  async function initialData() {
    const works: CandidateWork[] = await candidateService.getCandidateWorks();
    const currentWork = await candidateService.getCandidateCurrentWork();
    setInitialValues({
      isCurrentlyWorking: currentWork.isCurrentlyWorking ? "true" : "false",
      candidateWorkId: currentWork.candidateWorkId,
      recentWork: currentWork.recentWork ?? "",
      position: currentWork.position ?? "",
      salary: currentWork.salary > 0 ? currentWork.salary.toString() : "",
      companyDirection: currentWork.companyDirection ?? "",
      companyDirection2: currentWork.companyDirection2 ?? "",
      state: currentWork.state ?? "",
      city: currentWork.city ?? "",
      companyNeighborhood: currentWork.companyNeighborhood ?? "",
      workTime: currentWork.workTime ?? "",
      companyPhone: currentWork.companyPhone ?? "",
      companyPhoneCountryCode:
        currentWork.companyPhoneCountryCode || appState.countryCode,
      supervisorName: currentWork.supervisorName ?? "",
      actualJOB: true,
      hasPreviousJobs: works.length > 0 ? "SI" : "NO",
      workFunctionality: currentWork.workFunctionality ?? "",
      countryId: currentWork.countryId ?? null,
    });

    //setProvinces(province);
    setCandidateWorks(works);
    setIsLoading(false);
  }

  async function submit(values: ICandidateWorkForm) {
    if (
      (appState.applicationStateId != 23 && !appState.isAdmin) ||
      window.location.href.includes("profile")
    ) {
      setStep(step + 1);
      return;
    }
    const candidateWork: CandidateWork = {
      ...values,
      isCurrentlyWorking: values.isCurrentlyWorking == "true" ? true : false,
      salary: values.salary != "" ? Number(values.salary) : 0,
    };

    if (values.hasPreviousJobs == "SI" && candidateWorks.length == 0) {
      setOpenDialog(true);
      setDialogTitle("Error");
      setDialogDesc("Por favor agregar trabajo anterior");
      setDialogButton(<Button onClick={() => setOpenDialog(false)}>Ok</Button>);
      return;
    }

    try {
      setIsSaving(true);
      const work = await candidateService.saveCandidateWork(candidateWork);
      setIsSaving(false);
      setStep(step + 1);
    } catch {
      setIsSaving(false);
      setOpenDialog(true);
      setDialogTitle("Error");
      setDialogDesc("Ha ocurrido un error. Por favor intente mas tarde");
      setDialogButton(<Button onClick={() => setOpenDialog(false)}>Ok</Button>);
    }
  }

  useState(() => {
    initialData();
  });

  return (
    <Box sx={{ position: "relative", height: "100%" }}>
      {isLoading ? (
        <Box textAlign="center">
          <Box display="inline-block" textAlign="center">
            <CircularProgress color="secondary" size={50} sx={{ mr: "10px" }} />
            <Typography variant="body1">Cargando...</Typography>
          </Box>
        </Box>
      ) : (
        <Formik
          initialValues={initialValues}
          onSubmit={submit}
          validationSchema={
            !window.location.href.includes("profile")
              ? candidateWorkFormValidationScheme
              : undefined
          }
          validateOnMount={true}
        >
          {(props: FormikProps<ICandidateWorkForm>) => {
            const {
              values,
              touched,
              errors,
              handleBlur,
              handleChange,
              isSubmitting,
              setFieldValue,
              isValid,
            } = props;

            return (
              <Form>
                <Box textAlign="center">
                  <Typography
                    display="inline-block"
                    sx={{
                      mb: 3,
                      backgroundColor: "#00166B",
                      color: "white",
                      padding: "10px 30px",
                      borderRadius: "10px",
                      textTransform: "uppercase",
                    }}
                    variant="h4"
                  >
                    DATOS LABORABLES
                  </Typography>
                </Box>
                <Grid container spacing={1}>
                  {/* <Typography sx={{ mb: 3 }} variant="h4">
                    DATOS LABORABLES
                  </Typography> */}
                  <Box
                    bgcolor="primary"
                    sx={{
                      width: "100%",
                      bgcolor: "primary.main",
                    }}
                  >
                    <Typography sx={{ color: "white", px: 2, py: 1 }}>
                      Información del trabajo actual
                    </Typography>
                  </Box>

                  {/* Current Job*/}
                  <Grid item xs={12}>
                    <Paragraph title="¿Trabaja actualmente?" required />
                    <AppSelect
                      name="isCurrentlyWorking"
                      error={
                        errors.isCurrentlyWorking && touched.isCurrentlyWorking
                          ? true
                          : false
                      }
                      fullWidth
                      value={values.isCurrentlyWorking}
                      onChange={handleChange}
                    >
                      <MenuItem value="false">NO</MenuItem>
                      <MenuItem value="true">SI</MenuItem>
                    </AppSelect>
                  </Grid>
                  {/* Company Name*/}
                  {values.isCurrentlyWorking == "true" ? (
                    <Grid item lg={4} xs={12}>
                      <Paragraph
                        title="Nombre completo de la empresa"
                        required
                      />
                      <AppTextField
                        error={
                          errors.recentWork && touched.recentWork ? true : false
                        }
                        name="recentWork"
                        value={values.recentWork}
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>,
                        ) => handleInputChangeToUppercase(event, setFieldValue)}
                      />
                    </Grid>
                  ) : null}
                  {/* Position*/}
                  {values.isCurrentlyWorking == "true" ? (
                    <Grid item lg={4} xs={12}>
                      <Paragraph title="Posición" required />
                      <AppTextField
                        error={
                          errors.position && touched.position ? true : false
                        }
                        name="position"
                        value={values.position}
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>,
                        ) => handleInputChangeToUppercase(event, setFieldValue)}
                      />
                    </Grid>
                  ) : null}

                  {/* Salario*/}
                  {values.isCurrentlyWorking == "true" ? (
                    <Grid item lg={4} xs={12}>
                      <Paragraph
                        title={`Salario mensual ( ${appState.countryExchangeRateName} )`}
                        required
                      />
                      <AppTextField
                        type="number"
                        error={errors.salary && touched.salary ? true : false}
                        name="salary"
                        value={values.salary}
                        onChange={handleChange}
                      />
                    </Grid>
                  ) : null}
                  {/* Address*/}
                  {values.isCurrentlyWorking == "true" ? (
                    <Grid item lg={4} xs={12}>
                      <Paragraph
                        title="Dirección 1 (calle y número)"
                        required
                      />
                      <AppTextField
                        error={
                          errors.companyDirection && touched.companyDirection
                            ? true
                            : false
                        }
                        name="companyDirection"
                        value={values.companyDirection}
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>,
                        ) => handleInputChangeToUppercase(event, setFieldValue)}
                      />
                    </Grid>
                  ) : null}

                  {values.isCurrentlyWorking == "true" ? (
                    <Grid item lg={4} xs={12}>
                      <Paragraph title="Dirección 2 (apto., edificio, referencia)" />
                      <AppTextField
                        name="companyDirection2"
                        value={values.companyDirection2}
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>,
                        ) => handleInputChangeToUppercase(event, setFieldValue)}
                      />
                    </Grid>
                  ) : null}

                  {/*  Country  */}
                  {values.isCurrentlyWorking == "true" ? (
                    <Grid item lg={4} xs={12}>
                      <Paragraph title="País" required />
                      <AppSelect
                        name="countryId"
                        error={
                          errors.countryId && touched.countryId ? true : false
                        }
                        value={values.countryId}
                        onChange={handleChange}
                        fullWidth
                        isSearchable={true}
                        options={countries.map((el) => {
                          var option: IOption = {
                            label: el.name,
                            value: el.countryId.toString(),
                          };
                          return option;
                        })}
                      />
                    </Grid>
                  ) : null}

                  {/* Providences*/}
                  {values.isCurrentlyWorking == "true" ? (
                    <Grid item lg={4} xs={12}>
                      <Paragraph title="Provincia/Estado/Región" required />
                      <AppTextField
                        error={errors.state && touched.state ? true : false}
                        name="state"
                        value={values.state}
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>,
                        ) => handleInputChangeToUppercase(event, setFieldValue)}
                      />
                    </Grid>
                  ) : null}

                  {/* City */}
                  {values.isCurrentlyWorking == "true" ? (
                    <Grid item lg={4} xs={12}>
                      <Paragraph title="Ciudad/Localidad" required />
                      <AppTextField
                        error={errors.city && touched.city ? true : false}
                        name="city"
                        value={values.city}
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>,
                        ) => handleInputChangeToUppercase(event, setFieldValue)}
                      />
                    </Grid>
                  ) : null}
                  {/* Codigo postal */}
                  {values.isCurrentlyWorking == "true" ? (
                    <Grid item lg={4} xs={12}>
                      <Paragraph title="Código postal" />
                      <AppTextField
                        name="companyNeighborhood"
                        value={values.companyNeighborhood}
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>,
                        ) => handleInputChangeToUppercase(event, setFieldValue)}
                      />
                    </Grid>
                  ) : null}

                  {/* Fecha */}
                  {values.isCurrentlyWorking == "true" ? (
                    <Grid item lg={4} xs={12}>
                      <Paragraph title="Fecha de inicio" required />
                      <AppTextField
                        type="date"
                        error={
                          errors.workTime && touched.workTime ? true : false
                        }
                        name="workTime"
                        value={values.workTime}
                        onChange={handleChange}
                      />
                    </Grid>
                  ) : null}

                  {/* Telefono */}
                  {values.isCurrentlyWorking == "true" ? (
                    <Grid item lg={4} xs={12}>
                      <Paragraph title="Teléfono" required />
                      <AppPhoneField
                        countries={countries}
                        name="companyPhone"
                        error={
                          errors.companyPhone && touched.companyPhone
                            ? true
                            : false
                        }
                        countryId={
                          countries.find(
                            (country) =>
                              country.countryAlpha2 ===
                              values.companyPhoneCountryCode,
                          )?.countryId
                        }
                        phone={values.companyPhone ?? ""}
                        onBlur={handleBlur}
                        onCountryChange={(countryId) => {
                          const country = countries.find(
                            (item) => item.countryId.toString() === countryId,
                          );
                          setFieldValue(
                            "companyPhoneCountryCode",
                            country?.countryAlpha2 ?? "",
                          );
                        }}
                        onPhoneChange={(phone) => {
                          setFieldValue("companyPhone", phone);
                        }}
                      />
                    </Grid>
                  ) : null}

                  {/* Describe work functionality */}

                  {values.isCurrentlyWorking == "true" ? (
                    <Grid item xs={12}>
                      <Paragraph
                        title="Describa brevemente sus funciones "
                        required
                      />
                      <AppTextField
                        error={
                          errors.workFunctionality && touched.workFunctionality
                            ? true
                            : false
                        }
                        name="workFunctionality"
                        value={values.workFunctionality}
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>,
                        ) => handleInputChangeToUppercase(event, setFieldValue)}
                      />
                    </Grid>
                  ) : null}

                  {/* Previous Job*/}
                  <Grid item xs={12}>
                    <Paragraph title="¿Ha trabajado anteriormente?" required />
                    <AppSelect
                      name="hasPreviousJobs"
                      error={
                        errors.hasPreviousJobs && touched.hasPreviousJobs
                          ? true
                          : false
                      }
                      fullWidth
                      value={values.hasPreviousJobs}
                      onChange={handleChange}
                    >
                      <MenuItem value="NO">NO</MenuItem>
                      <MenuItem value="SI">SI</MenuItem>
                    </AppSelect>
                  </Grid>

                  {values.hasPreviousJobs == "SI" ? (
                    <Grid item xs={12}>
                      {appState.applicationStateId == 23 || appState.isAdmin ? (
                        <Button
                          sx={{ mb: 2 }}
                          variant="contained"
                          onClick={() => {
                            setCandidateWork({
                              isCurrentlyWorking: false,
                              candidateWorkId: 0,
                              recentWork: "",
                              position: "",
                              salary: 0,
                              companyDirection: "",
                              state: "",
                              city: "",
                              companyNeighborhood: "",
                              workTime: "",
                              companyPhone: "",
                              companyPhoneCountryCode: appState.countryCode,
                              supervisorName: "",
                              actualJOB: false,
                            });
                            setCandidatePreviousJobModal(true);
                          }}
                        >
                          Agregar
                        </Button>
                      ) : null}

                      <TableContainer component={Paper}>
                        <Table
                          //sx={{ minWidth: 650 }}
                          size="small"
                          aria-label="a dense table"
                        >
                          <TableHead>
                            <TableRow>
                              <StyledTableCell align="left">
                                Empresa
                              </StyledTableCell>
                              <StyledTableCell align="left">
                                Posicion
                              </StyledTableCell>
                              <StyledTableCell align="left">
                                Salario
                              </StyledTableCell>
                              <StyledTableCell align="left"></StyledTableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {candidateWorks.map((el) => (
                              <StyledTableRow
                                key={el.candidateWorkId}
                                sx={{
                                  "&:last-child td, &:last-child th": {
                                    border: 0,
                                  },
                                }}
                              >
                                <StyledTableCell align="left">
                                  {el.recentWork}
                                </StyledTableCell>

                                <StyledTableCell align="left">
                                  {el.position}
                                </StyledTableCell>

                                <StyledTableCell align="left">
                                  {el.salary}
                                </StyledTableCell>

                                <StyledTableCell align="left">
                                  {appState.applicationStateId == 23 ||
                                  appState.isAdmin ? (
                                    <Box>
                                      <IconButton
                                        sx={{ mr: 1 }}
                                        color="error"
                                        onClick={async () => {
                                          try {
                                            await candidateService.deleteCandidateWork(
                                              el.candidateWorkId,
                                            );
                                            var works =
                                              await candidateService.getCandidateWorks();
                                            setCandidateWorks(works);
                                          } catch {
                                            setOpenDialog(true);
                                            setDialogTitle("Error");
                                            setDialogDesc(
                                              "Ha ocurrido un error. Por favor intente mas tarde",
                                            );
                                            setDialogButton(
                                              <Button
                                                onClick={() =>
                                                  setOpenDialog(false)
                                                }
                                              >
                                                Ok
                                              </Button>,
                                            );
                                          }
                                        }}
                                      >
                                        <DeleteIcon />
                                      </IconButton>
                                      <IconButton
                                        color="success"
                                        onClick={async () => {
                                          setCandidateWork(el);
                                          setCandidatePreviousJobModal(true);
                                        }}
                                      >
                                        <EditIcon />
                                      </IconButton>
                                    </Box>
                                  ) : null}
                                </StyledTableCell>
                              </StyledTableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Grid>
                  ) : null}

                  {/* BUTTON */}
                  <Grid item xs={12}>
                    <Button
                      onClick={() => {
                        setStep(step - 1);
                      }}
                      color="customGrey"
                      variant="contained"
                      sx={{ mr: "10px", color: "white" }}
                    >
                      Atras
                    </Button>
                    <Button
                      onClick={() => {
                        if (!isValid) {
                          setIsValidForm(true);
                        }
                      }}
                      disabled={isSubmitting}
                      type="submit"
                      color="secondary"
                      variant="contained"
                      sx={{ bgcolor: "secondary.main" }}
                    >
                      {isSubmitting ? <>Guardando...</> : "Siguiente"}
                    </Button>
                  </Grid>

                  <ConfirmationDialog
                    maxWidth="sm"
                    open={isValidForm}
                    title={"Error"}
                    description={"Llenar todos los requisitos en rojo"}
                    buttons={
                      <Button onClick={() => setIsValidForm(false)}>Ok</Button>
                    }
                  />
                </Grid>
              </Form>
            );
          }}
        </Formik>
      )}
      <LoadingModal isLoading={isSaving} loadingMessage="Guardando" />

      <ConfirmationDialog
        maxWidth="sm"
        open={openDialog}
        title={dialogTitle}
        description={dialogDesc}
        buttons={dialogButton}
      />
      <Dialog fullWidth={true} maxWidth="md" open={candidatePreviousJobModal}>
        <DialogTitle>Trabajo anterior</DialogTitle>
        <DialogContent>
          <CandidatePreviousJobModal
            countries={countries}
            candidateWork={candidateWork}
            onSave={async (work: CandidateWork) => {
              const works = await candidateService.getCandidateWorks();
              setCandidateWorks(works);
              setCandidatePreviousJobModal(false);
            }}
            close={() => {
              setCandidatePreviousJobModal(false);
            }}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
}
export default CandidateWorkForm;
